import React, { useMemo, useState } from "react";

import {
  List,
  Show,
  DataTable,
  TextField,
  NumberField,
  BooleanField,
  FunctionField,
  TextInput,
  TabbedShowLayout,
  Tab,
  Pagination,
  TopToolbar,
  FilterButton,
  ColumnsButton,
  useRecordContext,
  useGetList,
  useGetPathForRecordCallback,
  ReferenceField,
  ReferenceInput,
  AutocompleteInput,
  Button,
} from "react-admin";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";

import { hasScope, SCOPE } from "../lib/auth";
import { DetailsButton } from "../lib/detailsButton";
import { SectionHeader } from "../lib/sectionHeader";
import { CustomDatetimeField } from "../lib/datetime";
import { AliasOrBarcodeReference } from "../lib/aliasWithBarcodeTooltip";
import { CustomTab } from "../lib/customTab";

// VolWithUnitField renders a volume together with its unit (e.g. "100 L"),
// following the FunctionField pattern of VolumeField in ../lib/volume.
const VolWithUnitField = ({
  source,
  unitSource,
  label,
}: {
  source: string;
  unitSource: string;
  label?: string;
}) => (
  <FunctionField
    source={source}
    label={label}
    render={(record: any) =>
      record?.[source]
        ? `${record[source]} ${record?.[unitSource] || ""}`.trim()
        : "\u00A0"
    }
  />
);

// TxnTable is the shared transaction data table, focused on the transfer
// source/destination details. It also renders inside the vessel and lot
// detail pages, where the surrounding resource context is not
// vintrace_transactions, so the row and Details links target the resource
// explicitly.
const TxnTable = (props: any) => {
  const getPathForRecord = useGetPathForRecordCallback();
  return (
    <DataTable
      bulkActionButtons={false}
      rowClick={(_id: any, _resource: any, record: any) =>
        getPathForRecord({
          link: "show",
          resource: "vintrace_transactions",
          record,
        })
      }
      {...props}
    >
      <DataTable.Col source="date" label="Date">
        <CustomDatetimeField source="date" label="Date" />
      </DataTable.Col>
      <DataTable.Col source="operationTypeName" label="Type">
        <TextField source="operationTypeName" label="Type" />
      </DataTable.Col>
      <DataTable.Col label="From Vessel" disableSort>
        <AliasOrBarcodeReference
          source="fromLocalVesselId"
          reference="vessels"
          label="From Vessel"
          sortable={false}
        />
      </DataTable.Col>
      <DataTable.Col label="From Lot" disableSort>
        <ReferenceField
          source="fromLocalLotId"
          reference="lots"
          label="From Lot"
          link="show"
        >
          <TextField source="name" />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col source="volOut" label="Volume Out">
        <VolWithUnitField source="volOut" unitSource="volOutUnit" />
      </DataTable.Col>
      <DataTable.Col label="To Vessel" disableSort>
        <AliasOrBarcodeReference
          source="toLocalVesselId"
          reference="vessels"
          label="To Vessel"
          sortable={false}
        />
      </DataTable.Col>
      <DataTable.Col label="To Lot" disableSort>
        <ReferenceField
          source="toLocalLotId"
          reference="lots"
          label="To Lot"
          link="show"
        >
          <TextField source="name" />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col source="volIn" label="Volume In">
        <VolWithUnitField source="volIn" unitSource="volInUnit" />
      </DataTable.Col>
      <DataTable.Col>
        <DetailsButton resource="vintrace_transactions" />
      </DataTable.Col>
    </DataTable>
  );
};

const txnFilters = [
  <TextInput
    source="operationType"
    label="Operation Type"
    placeholder="exact name, e.g. Transfer"
    resettable
  />,
  <ReferenceInput
    label="Lot"
    source="localLotID"
    reference="lots"
    perPage={100}
  >
    <AutocompleteInput
      optionText="name"
      label="Lot"
      filterToQuery={(searchText) => ({ search: searchText })}
      style={{ minWidth: 300 }}
    />
  </ReferenceInput>,
  <ReferenceInput
    label="Vessel"
    source="localVesselID"
    reference="vessels"
    perPage={100}
  >
    <AutocompleteInput
      optionText={(rec) => (rec.alias ? rec.alias : rec.barcode)}
      filterToQuery={(searchText) => ({ search: searchText })}
      label="Vessel"
      style={{ minWidth: 300 }}
    />
  </ReferenceInput>,
];

// VintraceTransactionList is the customer-wide list view, ordered by date.
export const VintraceTransactionList = () => (
  <List
    perPage={100}
    sort={{ field: "date", order: "ASC" }}
    pagination={<Pagination rowsPerPageOptions={[100, 200, 500, 1000]} />}
    filters={txnFilters}
    actions={
      <TopToolbar>
        <FilterButton />
        <ColumnsButton />
      </TopToolbar>
    }
    storeKey={false}
    empty={false}
  >
    <TxnTable sx={{ "& .RaDataTable-tableWrapper": { overflowX: "auto" } }} />
  </List>
);

// VintraceTransactionShow is the details page, with a custom tab showing the
// entire vintrace JSON blob, like the Custom tab on Vessels and Lots.
export const VintraceTransactionShow = () => (
  <Show>
    <TabbedShowLayout>
      <Tab label="Details">
        <CustomDatetimeField source="date" label="Date" />
        <TextField source="operationType" label="Type" />
        <TextField source="subOperation" label="Sub-operation" />
        <BooleanField source="reversed" label="Reversed" />
        <TextField source="workorder" label="Workorder" />
        <TextField source="winery" label="Winery" />

        <SectionHeader title="Source" />
        <AliasOrBarcodeReference
          source="fromLocalVesselId"
          reference="vessels"
          label="Vessel"
        />
        <TextField source="fromVesselName" label="Vessel (Vintrace)" />
        <ReferenceField
          source="fromLocalLotId"
          reference="lots"
          label="Lot"
          link="show"
        >
          <TextField source="name" />
        </ReferenceField>
        <TextField source="fromBatchName" label="Batch (Vintrace)" />
        <NumberField source="fromVolume" label="Volume Before" />
        <NumberField source="fromVolumeAfter" label="Volume After" />
        <VolWithUnitField
          source="volOut"
          unitSource="volOutUnit"
          label="Volume Out"
        />

        <SectionHeader title="Destination" />
        <AliasOrBarcodeReference
          source="toLocalVesselId"
          reference="vessels"
          label="Vessel"
        />
        <TextField source="toVesselName" label="Vessel (Vintrace)" />
        <ReferenceField
          source="toLocalLotId"
          reference="lots"
          label="Lot"
          link="show"
        >
          <TextField source="name" />
        </ReferenceField>
        <TextField source="toBatchName" label="Batch (Vintrace)" />
        <NumberField source="toVolume" label="Volume Before" />
        <NumberField source="toVolumeAfter" label="Volume After" />
        <VolWithUnitField
          source="volIn"
          unitSource="volInUnit"
          label="Volume In"
        />

        <NumberField source="lossVolume" label="Loss Volume" />
        <TextField source="lossReason" label="Loss Reason" />
      </Tab>
      <CustomTab label="Vintrace JSON" />
    </TabbedShowLayout>
  </Show>
);


// TxnTabList is the tab content for the Vessel and Lot detail pages: all
// transactions directly connected to the record (as source or destination
// vessel, or via the lot's vintrace batch id(s)). The filter object must be
// memoized by the caller to avoid refetch loops.
const TxnTabList = ({ filter }: { filter: object }) => {
  const [page, setPage] = useState(1);
  const perPage = 50;

  const { data, total, isPending } = useGetList("vintrace_transactions", {
    filter,
    sort: { field: "date", order: "ASC" },
    pagination: { page, perPage },
  });

  const nbPages = Math.max(1, Math.ceil((total || 0) / perPage));

  return (
    <>
      {data === undefined ? (
        <></>
      ) : data.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No transactions found.
        </Typography>
      ) : (
        <TxnTable data={data} total={total} isPending={isPending} />
      )}
      {total && total > perPage && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{ mt: 1 }}
        >
          <Button
            disabled={page <= 1 || isPending}
            onClick={() => setPage(page - 1)}
          >
            Prev
          </Button>
          <Typography variant="body2">
            Page {page} of {nbPages}
          </Typography>
          <Button
            disabled={page >= nbPages || isPending}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </Stack>
      )}
    </>
  );
};

// VesselVintraceTransactionsTab follows the CustomTab pattern: props are
// spread onto the Tab so the TabbedShowLayout can render it in the header
// (label only) and in the content (tab body). Admin only.
export const VesselVintraceTransactionsTab = (props: any) => {
  const record = useRecordContext();
  const filter = useMemo(
    () => (record ? { localVesselID: record.id } : {}),
    // Depend on record.id (not record, whose identity changes on every
    // render) so useGetList doesn't refetch on unrelated re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [record?.id],
  );

  if (!hasScope(SCOPE.ADMIN) || !record) {
    return null;
  }

  return (
    <Tab label="Vintrace Txs" {...props}>
      <TxnTabList filter={filter} />
    </Tab>
  );
};

// LotVintraceTransactionsTab follows the CustomTab pattern (see above),
// filtered on the lot's internal id. Admin only.
export const LotVintraceTransactionsTab = (props: any) => {
  const record = useRecordContext();
  const filter = useMemo(
    () => (record ? { localLotID: record.id } : {}),
    // Depend on record.id (not record, whose identity changes on every
    // render) so useGetList doesn't refetch on unrelated re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [record?.id],
  );

  if (!hasScope(SCOPE.ADMIN) || !record) {
    return null;
  }

  return (
    <Tab label="Vintrace Txs" {...props}>
      <TxnTabList filter={filter} />
    </Tab>
  );
};
